import React, { useEffect, useMemo, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { getPadAttachmentUrl } from "./api";
import { downloadFile, formatBytes, openFile, printFile } from "./fileActions";

const C = {
  primaryMd: "#1A6FB5",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
  soft: "#F7F9FC",
};

export const MAX_ATTACHMENTS = 10;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const iconFor = (filename, mime) => {
  const f = `${filename || ""} ${mime || ""}`.toLowerCase();
  if (f.includes("pdf")) return "file-text";
  if (/(png|jpe?g|gif|webp|bmp|image)/.test(f)) return "image";
  if (/(xls|sheet|csv)/.test(f)) return "grid";
  if (/(doc|word)/.test(f)) return "file-text";
  if (/(msg|eml|mail)/.test(f)) return "mail";
  return "paperclip";
};

const shortDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-CA");
};

/**
 * Files attached to one PAD entry — the paperwork behind the change.
 *
 * Controlled and fully staged: choosing a file adds it to a pending list, removing a saved one
 * marks it, and **nothing reaches storage until the modal is saved**. That is what lets someone
 * attach paperwork while recording a brand-new entry, and it means Cancel really cancels —
 * no half-created entry, no orphaned upload sitting in a bucket with nothing pointing at it.
 *
 * A staged file is previewable straight away through a local object URL, so the person can
 * check they picked the right document before committing to it.
 */
const PadAttachments = ({
  entryId,
  saved = [],
  staged = [],
  removedIds = [],
  onStage,
  onUnstage,
  onToggleRemove,
  voidCheque = null,
  canWrite = true,
  readOnly = false,
  busy = false,
  openPickerRef,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [actingOn, setActingOn] = useState("");
  const inputRef = useRef(null);

  // The section header owns the "Attach file" button; hand it a way to open our input
  // rather than rendering a second hidden one that could drift out of sync.
  useEffect(() => {
    if (!openPickerRef) return undefined;
    openPickerRef.current = () => inputRef.current && inputRef.current.click();
    return () => {
      openPickerRef.current = null;
    };
  }, [openPickerRef]);

  const savedLive = useMemo(() => (saved || []).filter((a) => a && !a.removed), [saved]);
  const removed = useMemo(() => new Set(removedIds || []), [removedIds]);
  const editable = canWrite && !readOnly;

  // Local URLs so a just-picked file can be opened before it has ever been uploaded.
  const stagedUrls = useMemo(() => (staged || []).map((f) => URL.createObjectURL(f)), [staged]);
  useEffect(() => () => stagedUrls.forEach((u) => URL.revokeObjectURL(u)), [stagedUrls]);

  const keptCount = savedLive.filter((a) => !removed.has(a.attachment_id)).length + (staged || []).length;

  const stage = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (keptCount + list.length > MAX_ATTACHMENTS) {
      toast.error(`An entry can hold at most ${MAX_ATTACHMENTS} attachments.`);
      return;
    }
    const tooBig = list.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is larger than 10 MB.`);
      return;
    }
    onStage && onStage(list);
    if (inputRef.current) inputRef.current.value = "";
  };

  /** Saved files need a short-lived signed link; staged ones already have a local URL. */
  const withSavedUrl = async (att, fn) => {
    setActingOn(att.attachment_id);
    try {
      const { data } = await getPadAttachmentUrl(entryId, att.attachment_id);
      const url = data?.data?.url;
      if (!url) throw new Error("No link came back for this file");
      await fn(url);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Could not open the file");
    } finally {
      setActingOn("");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (editable && !busy) stage(e.dataTransfer.files);
  };

  const Row = ({ icon, title, meta, actions, tone, strike }) => (
    <div className={`pa-row${strike ? " off" : ""}`}>
      <span className="pa-icon" style={tone ? { background: "#EEF4FB", color: C.primaryMd } : undefined}>
        <FeatherIcon icon={icon} size={15} />
      </span>
      <span className="pa-main">
        <span className="pa-name" title={title}>{title}</span>
        {meta ? <span className="pa-meta">{meta}</span> : null}
      </span>
      <span className="pa-actions">{actions}</span>
    </div>
  );

  return (
    <div>
      <style>{`
        .pa-row { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border: 1px solid ${C.border};
          border-radius: 9px; background: #fff; margin-bottom: 8px; }
        .pa-row.off { opacity: .55; }
        .pa-row.off .pa-name { text-decoration: line-through; }
        .pa-row.new { border-color: #BFDBFE; background: #F8FBFF; }
        .pa-icon { width: 30px; height: 30px; border-radius: 8px; flex: none; display: inline-flex; align-items: center;
          justify-content: center; background: #F1F5F9; color: ${C.muted}; }
        .pa-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .pa-name { font-size: 13px; font-weight: 600; color: ${C.text}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pa-meta { font-size: 11.5px; color: ${C.muted}; }
        .pa-pill { font-size: 10px; font-weight: 700; padding: 1px 7px; border-radius: 999px; margin-left: 6px;
          background: #E0EDFB; color: #1D4ED8; text-transform: uppercase; letter-spacing: .04em; }
        .pa-pill.rm { background: #FDE8E6; color: #C62828; }
        .pa-actions { display: flex; gap: 4px; flex: none; }
        .pa-btn { border: 1px solid ${C.inputBorder}; background: #fff; border-radius: 7px; width: 30px; height: 30px;
          display: inline-flex; align-items: center; justify-content: center; color: ${C.muted}; cursor: pointer;
          transition: border-color .12s, color .12s, background .12s; }
        .pa-btn:hover:not(:disabled) { border-color: ${C.primaryMd}; color: ${C.primaryMd}; background: #F8FBFF; }
        .pa-btn:disabled { opacity: .45; cursor: not-allowed; }
        .pa-btn.danger:hover:not(:disabled) { border-color: #C62828; color: #C62828; background: #FEF3F3; }
        .pa-drop { border: 1.5px dashed ${C.inputBorder}; border-radius: 10px; padding: 16px; text-align: center;
          background: ${C.soft}; cursor: pointer; transition: border-color .12s, background .12s; }
        .pa-drop:hover, .pa-drop.over { border-color: ${C.primaryMd}; background: #F4F9FF; }
        .pa-drop.disabled { cursor: not-allowed; opacity: .7; }
        .pa-drop-title { font-size: 13px; font-weight: 600; color: ${C.text}; }
        .pa-drop-hint { font-size: 11.5px; color: ${C.muted}; margin-top: 3px; }
        .pa-empty { font-size: 12px; color: ${C.muted}; padding: 2px 0 10px; }
        .pa-blank { border: 1.5px dashed ${C.inputBorder}; border-radius: 12px; background: ${C.soft};
          padding: 26px 18px; display: flex; flex-direction: column; align-items: center; gap: 6px;
          text-align: center; cursor: pointer; transition: border-color .12s, background .12s; }
        .pa-blank:hover, .pa-blank.over { border-color: ${C.primaryMd}; background: #F4F9FF; }
        .pa-blank-ic { width: 44px; height: 44px; border-radius: 999px; background: #EAF2FD; color: ${C.primaryMd};
          display: inline-flex; align-items: center; justify-content: center; margin-bottom: 2px; }
        .pa-blank-t { font-size: 13.5px; font-weight: 700; color: ${C.text}; }
        .pa-blank-s { font-size: 12px; color: ${C.muted}; max-width: 380px; line-height: 1.4; }
      `}</style>

      {voidCheque ? (
        <Row
          icon="shield"
          tone
          title="PAD / Void Cheque"
          meta={`From Documents${voidCheque.lastUpdated ? ` · updated ${voidCheque.lastUpdated}` : ""} · managed in the Documents tab`}
          actions={null}
        />
      ) : null}

      {savedLive.length === 0 && (staged || []).length === 0 && !voidCheque ? (
        <div
          className={`pa-blank${dragOver ? " over" : ""}`}
          onClick={() => editable && !busy && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <span className="pa-blank-ic"><FeatherIcon icon="file" size={20} /></span>
          <span className="pa-blank-t">No files attached yet</span>
          <span className="pa-blank-s">
            {editable
              ? "Drop the email, notice or signed form here — it uploads when you save."
              : "Nothing was attached to this entry."}
          </span>
        </div>
      ) : null}

      {/* Already saved on the entry. */}
      {savedLive.map((a) => {
        const acting = actingOn === a.attachment_id;
        const isRemoved = removed.has(a.attachment_id);
        return (
          <Row
            key={a.attachment_id}
            icon={iconFor(a.filename, a.mime)}
            strike={isRemoved}
            title={a.filename}
            meta={
              <>
                {[formatBytes(a.size), a.uploaded_by?.name, shortDate(a.uploaded_at)].filter(Boolean).join(" · ")}
                {isRemoved ? <span className="pa-pill rm">Removed on save</span> : null}
              </>
            }
            actions={
              <>
                <button type="button" className="pa-btn" title="View" disabled={acting || busy} onClick={() => withSavedUrl(a, (url) => openFile(url))}>
                  <FeatherIcon icon="eye" size={14} />
                </button>
                <button type="button" className="pa-btn" title="Download" disabled={acting || busy} onClick={() => withSavedUrl(a, (url) => downloadFile(url, a.filename))}>
                  <FeatherIcon icon="download" size={14} />
                </button>
                <button type="button" className="pa-btn" title="Print" disabled={acting || busy} onClick={() => withSavedUrl(a, (url) => printFile(url, a.filename, a.mime))}>
                  <FeatherIcon icon="printer" size={14} />
                </button>
                {editable ? (
                  <button
                    type="button"
                    className={isRemoved ? "pa-btn" : "pa-btn danger"}
                    title={isRemoved ? "Keep this file" : "Remove"}
                    disabled={acting || busy}
                    onClick={() => onToggleRemove && onToggleRemove(a.attachment_id)}
                  >
                    <FeatherIcon icon={isRemoved ? "rotate-ccw" : "trash-2"} size={14} />
                  </button>
                ) : null}
              </>
            }
          />
        );
      })}

      {/* Picked but not uploaded yet — they go up when the entry is saved. */}
      {(staged || []).map((f, i) => (
        <div className="pa-row new" key={`${f.name}-${i}`}>
          <span className="pa-icon">
            <FeatherIcon icon={iconFor(f.name, f.type)} size={15} />
          </span>
          <span className="pa-main">
            <span className="pa-name" title={f.name}>{f.name}</span>
            <span className="pa-meta">
              {formatBytes(f.size)}
              <span className="pa-pill">Attaches on save</span>
            </span>
          </span>
          <span className="pa-actions">
            <button type="button" className="pa-btn" title="View" disabled={busy} onClick={() => openFile(stagedUrls[i])}>
              <FeatherIcon icon="eye" size={14} />
            </button>
            <button type="button" className="pa-btn" title="Print" disabled={busy} onClick={() => printFile(stagedUrls[i], f.name, f.type)}>
              <FeatherIcon icon="printer" size={14} />
            </button>
            {editable ? (
              <button type="button" className="pa-btn danger" title="Remove" disabled={busy} onClick={() => onUnstage && onUnstage(i)}>
                <FeatherIcon icon="x" size={14} />
              </button>
            ) : null}
          </span>
        </div>
      ))}

      {editable ? (
        <>
          <div
            className={`pa-drop ${dragOver ? "over" : ""} ${busy ? "disabled" : ""}`}
            style={savedLive.length === 0 && (staged || []).length === 0 && !voidCheque ? { display: "none" } : undefined}
            onClick={() => !busy && inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            <FeatherIcon icon="upload-cloud" size={20} color={C.muted} />
            <div className="pa-drop-title">Drop files here, or click to choose</div>
            <div className="pa-drop-hint">
              Up to {MAX_ATTACHMENTS} files, 10 MB each — the email, notice or signed form behind this change.
              {entryId ? "" : " They upload when you save this entry."}
            </div>
          </div>
          <input ref={inputRef} type="file" multiple style={{ display: "none" }} onChange={(e) => stage(e.target.files)} />
        </>
      ) : null}
    </div>
  );
};

export default PadAttachments;
